<template>
  <header class="nav-header v-centerer" :class="elClasses">
    <h1 class="basic-blue-title nav-header__title v-centerer">
      <Icon
        v-if="backButtonAction"
        class="nav-header__back-button"
        @click.native="backButtonAction"
      >
        {{ arrowDownLine }}
      </Icon>
      <span class="nav-header__title__text">{{ pageTitle }}</span>
    </h1>
    <!-- Problem report -->
    <RouterLink
      v-if="!noCheckLoginPage && !loginPage"
      :to="getPbReportingLink(0)"
      class="generic-border generic-border--text-btn generic-border--blue nav-header__problem"
    >
      <Icon>{{ addExtraBold }}</Icon>
      <span>{{ $t('message.PROBLEM') }}</span>
    </RouterLink>
    <!-- Action report -->
    <RouterLink
      v-if="!noCheckLoginPage && !loginPage"
      :to="getActionCreationLink(0)"
      class="generic-border generic-border--text-btn generic-border--blue nav-header__action"
    >
      <Icon>{{ addExtraBold }}</Icon>
      <span>{{ $t('message.ACTION') }}</span>
    </RouterLink>
    <!-- Search bar -->
    <div
      v-if="!noCheckLoginPage && !loginPage && !$store.state.device.isSmartphonePortrait"
      class="search-wrapper v-centerer generic-border generic-border--thin-border generic-border--focus-shadow"
    >
      <Icon>{{ searchMedium }}</Icon>
      <input
        v-model="searchInput"
        type="search"
        :placeholder="$t('message.SEARCH_PH')"
        @focus="searchFocus()"
        @blur="searchBlur()"
        @input="function(e) { searchInput = e.target.value }"
      >
      <!-- Search results  -->
      <SearchResults :search="searchInput" :show="focusOnSearch" />
    </div>
    <!-- Left icons group -->
    <div v-if="!noCheckLoginPage && !loginPage" class="nav-header-right">
      <NotificationMenu ref="notifComponent" />
      <AccountMenu />
    </div>
  </header>
</template>

<script>
/**
 * Main header
 *
 * @vue-prop {Object} [pageComponent] - Current view component. Used to display the title of the page.
 *
 * @vue-event {} iaClick - Sent on click on Neo
 */

import $ from 'jquery';
// Components
import AccountMenu from '@/components/account-menu.vue';
import Icon from '@/components/icon.vue';
import NotificationMenu from '@/components/notification-menu.vue';
import SearchResults from '@/components/search-results.vue';
// Utils
import { addExtraBold, arrowDownLine, searchMedium } from '@/assets/icons';

export default {

  name: 'NavHeader',

  components: {
    AccountMenu,
    Icon,
    NotificationMenu,
    SearchResults,
  },

  props: {
    pageComponent: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      searchMedium,
      // Search data
      searchInput: '',
      focusOnSearch: false,
      elWidth: 10,
    };
  },

  computed: {
    /** Object: Classes applied to this.$el */
    elClasses() {
      return {
        'nav-header--in-embedded-app': this.appEmbedded,
      };
    },
    noCheckLoginPage() {
      return !!this.$route.meta.noCheckLogin;
    },
    loginPage() {
      return !!this.$route.meta.loginPage;
    },
    /** String: Title of the page */
    pageTitle() {
      return this.pageComponent ? this.pageComponent.pageTitle || '' : '';
    },
    /** Boolean: Wether the current page need a back button or not */
    backButtonAction() {
      return this.pageComponent && this.pageComponent.pageBackButtonAction;
    },
  },

  watch: {
    currentTeam() {
      // Size of the header changes when the team is changed
      this.updateComponentWidth();
    },
  },

  mounted() {
    this.updateComponentWidth();
    $(window).on(this.resizeEventName, this.updateComponentWidth);
  },

  updated() {
    this.updateComponentWidth();
  },

  methods: {
    /** Focus on the search input */
    searchFocus() {
      this.focusOnSearch = true;
    },
    updateComponentWidth() {
      document.documentElement.style.setProperty('--header-width', `${this.$el.offsetWidth}px`);
    },
    searchBlur() {
      setTimeout(() => {
        this.focusOnSearch = false;
      }, 300);
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vars.scss';

.nav-header {
  position: relative;
  padding-right: 20px;
  height: $header-height;
  padding-left: $nav-width;
  width: 100%;
  box-sizing: border-box;
  background: white;
  border-bottom: solid 1px $color-gray-background;

  &__back-button {
    margin-right: 1rem;
    transform: rotate(90deg);
    width: 1rem;
    height: 1rem;
    padding: .5rem;
    border-radius: $medium-border-radius;
    cursor: pointer;

    &:hover {
      background-color: $color-gray-background-hover;
    }
  }

  &--in-embedded-app {
    padding-left: 0;

    > *:not(h1.basic-blue-title) {
      display: none;
    }
  }

  // Right buttons
  &-right {
    margin-right: -0.5em;
    display: flex;
    align-items: center;
    padding: 0.5em 0;
    height: calc(100% - 1em);

    & > * {
      vertical-align: top;
    }
  }

  &__title.basic-blue-title {
    margin: 0 1.7rem;
    flex: 1;
    min-width: 0px;

    .icon {
      flex: 0 0 auto;
    }

    .nav-header__title__text {
      min-width: 0px;
      flex: 1;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  .generic-border--blue {
    margin-right: 0.6rem;
  }

  // Search
  .search-wrapper {
    margin-left: 0.6rem;
    width: 11.5rem;
    height: 40px;

    .icon {
      flex: 0 0 auto;
      width: .9rem;
    }

    input {
      width: calc(100% - 2.3rem);
      background: transparent;
      border: none;
      padding-left: .9rem;
      height: 40px;
      box-sizing: border-box;
      // Outline made using :focus-within on the search-wrapper
      outline: none;
    }

    input::placeholder {
      color: $color-shizen-gray;
    }
  }

  .top-nav-button {
    border-radius: $main-border-radius;
    padding: .5rem .5rem;
    margin-left: .5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);

    &.account-menu {
      padding-right: 0;
    }
  }
}

@media (max-width: 1280px) {
  .nav-header {

    .search-wrapper {
      margin-left: 1.6em;
    }
  }
}

/* Smartphone landscape */
@media (max-width: $smartphone-landscape) {
  .nav-header {
    padding-left: $smartphone-nav-width;
    height: $smartphone-header-height;
    padding-right: .7rem;

    &__title.basic-blue-title {
      margin-left: .7rem;
      font-size: 1.3rem;
    }

    &__title.basic-blue-title {
      margin: 0 0.7rem;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    &__menu-button {

      &__text {
        display: none;
      }
    }

    .search-wrapper {
      height: calc(100% - 1em);
      min-width: 0;
      margin-left: 3em;
      margin: 0.5em 0;

      img {
        width: 0px;
      }

      input {
        width: 88px;
        margin-left: 0px;
        height: 25px;
      }

    }

    .top-nav-button {
      padding: 0.5em;
      margin-left: .2rem;
    }

    .generic-border--blue {
      padding: 0 .7rem;
      height: 2.5rem;
      margin-right: 0.3rem;

      .icon {
        margin-right: .5rem;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .nav-header {

    .search-wrapper {
      display: none;

      input {
        width: 80px;
        margin-left: 5px;
      }
    }

    .top-nav-button {
      padding: 0.3em;
      margin-left: .2rem;
    }
  }
}
</style>
